import React, { createContext, useContext, useMemo } from 'react';
import { FeatureFlags } from '@fabric-ui/components';
import { useAvContext } from '../../context/AvContextProvider';
import { DataContextType } from '../../context/types';

export interface FilterConfig {
  size?: 'xSmall' | 'small';
  isVertical?: boolean;
  disabled?: boolean;
  showAdvanced?: boolean;
  enableQueryObj?: boolean;
  activeProjName?: string;
  displayRecommendedFields?: boolean;
}

interface FilterConfigContextValue {
  size: 'xSmall' | 'small';
  isVertical: boolean;
  disabled: boolean;
  showAdvanced: boolean;
  enableQueryObj: boolean;
  activeProjName?: string;
}

interface FilterFeaturesContextValue {
  isRecommendedFieldsEnabled: boolean;
  isTopFields: boolean;
  isPopulatedFields: boolean;
  showErrorToast: boolean;
}

interface FilterDataContextValue {
  accountEntities: DataContextType['accountEntities'];
  measurements: DataContextType['measurements'];
}

export const FilterConfigContext = createContext<FilterConfigContextValue | null>(null);

export const filterConfigKeys: (keyof FilterConfig)[] = [
  'size',
  'isVertical',
  'disabled',
  'showAdvanced',
  'enableQueryObj',
  'activeProjName',
  'displayRecommendedFields',
];

export const pickFilterConfig = <T extends object>(props: T): [FilterConfig, T] => {
  const config: any = {};
  const rest: any = {};
  (Object.keys(props) as Array<keyof T>).forEach(key => {
    const stringKey = key as string;
    if (filterConfigKeys.includes(stringKey as any)) {
      const value = (props as any)[stringKey];
      if (value !== undefined) config[stringKey] = value;
    } else {
      rest[stringKey] = (props as any)[stringKey];
    }
  });
  return [config as FilterConfig, rest as T];
};

const FilterFeaturesContext = createContext<FilterFeaturesContextValue | null>(null);
const FilterDataContext = createContext<FilterDataContextValue | null>(null);

export const useFilterConfig = () => {
  const context = useContext(FilterConfigContext);
  if (!context) throw new Error('useFilterConfig must be used within a FilterProvider or FilterConfigProvider');
  return context;
};

export const useFilterFeatures = () => {
  const context = useContext(FilterFeaturesContext);
  if (!context) throw new Error('useFilterFeatures must be used within a FilterProvider');
  return context;
};

export const useFilterContext = () => {
  const context = useContext(FilterDataContext);
  if (!context) throw new Error('useFilterContext must be used within a FilterProvider or FilterConfigProvider');
  return context;
};

const useFilterConfigValue = (config: FilterConfig) =>
  useMemo(
    () => ({
      size: config.size ?? 'small',
      isVertical: config.isVertical ?? true,
      disabled: config.disabled ?? false,
      showAdvanced: config.showAdvanced ?? false,
      enableQueryObj: config.enableQueryObj ?? false,
      activeProjName: config.activeProjName,
    }),
    [config.activeProjName, config.disabled, config.enableQueryObj, config.isVertical, config.showAdvanced, config.size]
  );

const useFilterFeaturesValue = (config: FilterConfig) => {
  const { accountData, featureFlags } = useAvContext();
  const isRecommendedFieldsEnabled = accountData?.flags?.isRecommendedFieldsEnabled ?? false;

  return useMemo(
    () => ({
      isRecommendedFieldsEnabled,
      isTopFields: !!config.displayRecommendedFields && isRecommendedFieldsEnabled,
      isPopulatedFields: !!config.displayRecommendedFields && isRecommendedFieldsEnabled,
      showErrorToast: !!featureFlags[FeatureFlags.ShowServerErrorInToast],
    }),
    [config.displayRecommendedFields, featureFlags, isRecommendedFieldsEnabled]
  );
};

const FilterDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accountEntities, measurements } = useAvContext();
  const value = useMemo(() => ({ accountEntities, measurements }), [accountEntities, measurements]);
  return <FilterDataContext.Provider value={value}>{children}</FilterDataContext.Provider>;
};

interface FilterProviderProps {
  config: FilterConfig;
  children: React.ReactNode;
}

const FilterFeaturesProvider: React.FC<FilterProviderProps> = ({ config, children }) => {
  const value = useFilterFeaturesValue(config);
  return <FilterFeaturesContext.Provider value={value}>{children}</FilterFeaturesContext.Provider>;
};

export const FilterConfigProvider: React.FC<FilterProviderProps> = ({ config, children }) => {
  const value = useFilterConfigValue(config);
  return (
    <FilterConfigContext.Provider value={value}>
      <FilterDataProvider>{children}</FilterDataProvider>
    </FilterConfigContext.Provider>
  );
};

export const FilterProvider: React.FC<FilterProviderProps> = ({ config, children }) => (
  <FilterConfigProvider config={config}>
    <FilterFeaturesProvider config={config}>{children}</FilterFeaturesProvider>
  </FilterConfigProvider>
);
